// apps/web/src/lib/whatsapp-api.ts
import { api, apiClient, attachmentsApi } from './api'
import type {
  WaSettings,
  UpdateWaSettingsPayload,
  WaSenderRow,
  WaSenderPayload,
  WaTemplateRow,
  WaTemplateSyncResult,
  WaCreateTemplatePayload,
  WaCampaignRow,
  WaCampaignPayload,
  WaEnrollmentSummary,
  WaInboxThreadRow,
  WaThreadDetail,
  WaReplyPayload,
  WaDirectPayload,
  WaMessage,
  WaTagRow,
  WaAudiencePreview,
} from '@/types/whatsapp'

// Minimal shape of the deal returned by POST /whatsapp/inbox/:conversationId/deal — the endpoint
// takes no body (server derives name/contact from the conversation) and the caller only needs
// the id to link/navigate to it.
export interface WaConvertedDeal {
  _id: string
  [key: string]: unknown
}

export const whatsappApi = {
  // Per-org Meta app config
  getSettings: () =>
    apiClient.get<WaSettings>('/whatsapp/settings'),
  updateSettings: (data: UpdateWaSettingsPayload) =>
    apiClient.put<WaSettings>('/whatsapp/settings', data),
  // Clears this org's cooldown suppressions (opt-out/failed are kept). Returns a bare
  // { cleared } body (no `data` envelope), so this uses the raw axios client like the inbox routes.
  clearCooldowns: () =>
    api.post<{ cleared: number }>('/whatsapp/settings/clear-cooldowns'),

  // Senders (numbers)
  listSenders: () =>
    apiClient.get<WaSenderRow[]>('/whatsapp/senders'),
  createSender: (data: WaSenderPayload) =>
    apiClient.post<WaSenderRow>('/whatsapp/senders', data),
  updateSender: (id: string, data: WaSenderPayload) =>
    apiClient.patch<WaSenderRow>(`/whatsapp/senders/${id}`, data),
  deleteSender: (id: string) =>
    apiClient.delete(`/whatsapp/senders/${id}`),

  // Templates
  syncTemplates: (senderId: string) =>
    apiClient.post<WaTemplateSyncResult>(`/whatsapp/templates/${senderId}/sync`, {}),
  listTemplates: (senderId: string) =>
    apiClient.get<WaTemplateRow[]>(`/whatsapp/templates/${senderId}`),
  // ALL templates (any status) for the Settings → WhatsApp template-management list — distinct
  // from listTemplates above, which several send-time pick-lists (campaign builder, compose,
  // inbox reply) rely on returning ONLY approved+supported templates.
  listAllTemplates: (senderId: string) =>
    apiClient.get<WaTemplateRow[]>(`/whatsapp/templates/${senderId}/all`),
  // Authors + submits a new TEXT-based template to Meta — returns the freshly-upserted row
  // (status PENDING until Meta's async review lands via the status-update webhook).
  createTemplate: (senderId: string, data: WaCreateTemplatePayload) =>
    apiClient.post<WaTemplateRow>(`/whatsapp/templates/${senderId}`, data),

  // Campaigns
  listCampaigns: () =>
    apiClient.get<WaCampaignRow[]>('/whatsapp/campaigns'),
  getCampaign: (id: string) =>
    apiClient.get<WaCampaignRow>(`/whatsapp/campaigns/${id}`),
  createCampaign: (data: WaCampaignPayload) =>
    apiClient.post<WaCampaignRow>('/whatsapp/campaigns', data),
  updateCampaign: (id: string, data: WaCampaignPayload) =>
    apiClient.patch<WaCampaignRow>(`/whatsapp/campaigns/${id}`, data),
  sendCampaign: (id: string) =>
    apiClient.post<WaEnrollmentSummary>(`/whatsapp/campaigns/${id}/send`, {}),
  pauseCampaign: (id: string) =>
    apiClient.post<{ ok: true }>(`/whatsapp/campaigns/${id}/pause`, {}),
  resumeCampaign: (id: string) =>
    apiClient.post<{ ok: true }>(`/whatsapp/campaigns/${id}/resume`, {}),
  listTags: (q?: string) =>
    apiClient.get<WaTagRow[]>('/whatsapp/campaigns/tags', { q }),
  getAudiencePreview: (tag: string, senderId: string) =>
    apiClient.get<WaAudiencePreview>('/whatsapp/campaigns/audience', { tag, senderId }),

  // --- Inbox ---
  // NOTE: unlike every other whatsapp endpoint, these three routes return a bare body (no
  // { data } envelope) — call the raw axios instance directly so callers do `r.data` and get the
  // array/object itself, not `apiClient.get`'s extra (nonexistent) unwrap.
  listThreads: (params?: { senderId?: string; unread?: boolean }) =>
    api.get<WaInboxThreadRow[]>('/whatsapp/inbox', { params }),
  listUnmatched: () =>
    api.get<WaInboxThreadRow[]>('/whatsapp/inbox/unmatched'),
  getUnreadCount: () =>
    api.get<{ count: number }>('/whatsapp/inbox/unread-count'),

  getThread: (conversationId: string) =>
    apiClient.get<WaThreadDetail>(`/whatsapp/inbox/${conversationId}`),
  reply: (conversationId: string, payload: WaReplyPayload) =>
    apiClient.post<WaMessage>(`/whatsapp/inbox/${conversationId}/reply`, payload),
  convertToDeal: (conversationId: string) =>
    apiClient.post<WaConvertedDeal>(`/whatsapp/inbox/${conversationId}/deal`, {}),
  deleteConversation: (conversationId: string) =>
    apiClient.delete(`/whatsapp/inbox/${conversationId}`),

  // --- Direct (one-off, not tied to an existing conversation — Compose WhatsApp) ---
  directSend: (contactId: string, payload: WaDirectPayload) =>
    apiClient.post<WaMessage>(`/whatsapp/direct/${contactId}`, payload),

  // --- Header-media uploads (media-header templates, Phase 1) ---
  // Reuses the generic attachments upload endpoint (attachmentsApi.upload posts multipart to
  // /attachments/upload) rather than a bespoke whatsapp endpoint — same file-storage/validation
  // path every other entity's attachments go through.
  //
  // A campaign step's media header is uploaded against EntityType.WA_CAMPAIGN, entityId = the
  // CAMPAIGN's own id — so the campaign must already be saved (have a real `_id`) before a step
  // can get a header image; see WaCampaignBuilderView's `canEdit && !isNew` gate.
  uploadCampaignHeaderMedia: async (campaignId: string, file: File): Promise<{ _id: string }> => {
    const res = await attachmentsApi.upload(file, 'wa_campaign', campaignId)
    return res.data
  },
  // A one-off/direct send has no persistent campaign step to attach to — uploaded against
  // EntityType.CONTACT, entityId = the recipient contact (which already exists at compose time).
  uploadDirectHeaderMedia: async (contactId: string, file: File): Promise<{ _id: string }> => {
    const res = await attachmentsApi.upload(file, 'contact', contactId)
    return res.data
  },
}
